/**
 * Teslyn drawing a PDF's pages itself, for a device whose browser cannot show a whole PDF inside a page (an iPad, an
 * iPhone, a phone; `pdfViewFor`). pdf.js from Teslyn Cloud's own files. Its legacy build: the main build needs the
 * newest browsers, and a client's iPad may be years behind. Only the pages near the screen hold a picture, because
 * Safari stops drawing once canvases take too much memory and a weekly report has many large pages.
 */
import { useEffect, useRef, useState, type ReactElement } from 'react';
import { Loading } from '../components/States.tsx';

/** The parts of a pdf.js page this uses. */
export interface PdfPage {
  getViewport(o: { readonly scale: number }): { readonly width: number; readonly height: number };
  render(o: { canvas: HTMLCanvasElement; viewport: unknown }): {
    readonly promise: Promise<void>;
    cancel(): void;
  };
}

/** A PDF open in pdf.js. */
export interface PdfDocument {
  readonly numPages: number;
  getPage(n: number): Promise<PdfPage>;
  destroy(): Promise<void> | void;
}

export type OpenPdf = (bytes: Uint8Array) => Promise<PdfDocument>;

interface PdfJs {
  readonly GlobalWorkerOptions: { workerSrc: string };
  getDocument(o: { data: Uint8Array; isEvalSupported: boolean }): { readonly promise: Promise<PdfDocument> };
}

const openWithPdfJs: OpenPdf = async (bytes) => {
  const [lib, worker] = await Promise.all([
    import('pdfjs-dist/legacy/build/pdf.mjs') as unknown as Promise<PdfJs>,
    import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url'),
  ]);
  lib.GlobalWorkerOptions.workerSrc = worker.default;
  // no eval: the Content-Security-Policy refuses it
  return lib.getDocument({ data: bytes, isEvalSupported: false }).promise;
};

/** Safari's limit for one canvas is about 16.7 million pixels. */
const MAX_PAGE_PIXELS = 16_000_000;
const MAX_PIXEL_RATIO = 2;

export function pageScale(pageWidth: number, pageHeight: number, cssWidth: number, ratio: number): number {
  const wanted = (cssWidth * Math.min(ratio, MAX_PIXEL_RATIO)) / pageWidth;
  return Math.min(wanted, Math.sqrt(MAX_PAGE_PIXELS / (pageWidth * pageHeight)));
}

function Page({
  page,
  number,
  of,
  onFail,
}: {
  readonly page: PdfPage;
  readonly number: number;
  readonly of: number;
  readonly onFail: () => void;
}): ReactElement {
  const canvas = useRef<HTMLCanvasElement>(null);
  const size = page.getViewport({ scale: 1 });

  useEffect(() => {
    const el = canvas.current;
    if (!el) return undefined;
    let task: ReturnType<PdfPage['render']> | null = null;
    const draw = (): void => {
      if (task) return;
      const css = el.parentElement?.clientWidth || size.width;
      const viewport = page.getViewport({
        scale: pageScale(size.width, size.height, css, window.devicePixelRatio || 1),
      });
      el.width = Math.floor(viewport.width);
      el.height = Math.floor(viewport.height);
      task = page.render({ canvas: el, viewport });
      task.promise.catch((e: unknown) => {
        if ((e as { name?: string }).name !== 'RenderingCancelledException') onFail();
      });
    };
    const clear = (): void => {
      task?.cancel();
      task = null;
      el.width = 0;
      el.height = 0;
    };
    if (typeof IntersectionObserver === 'undefined') {
      draw();
      return clear;
    }
    const seen = new IntersectionObserver(
      ([e]) => {
        if (e?.isIntersecting) draw();
        else clear();
      },
      { rootMargin: '100% 0px' },
    );
    seen.observe(el);
    return () => {
      seen.disconnect();
      clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one page, drawn while it is near the screen
  }, [page]);

  return (
    <div className="docview__page" style={{ aspectRatio: `${size.width} / ${size.height}` }}>
      <canvas ref={canvas} role="img" aria-label={`Page ${number} of ${of}`} />
    </div>
  );
}

export function PdfPages({
  blob,
  onFail,
  open = openWithPdfJs,
}: {
  readonly blob: Blob;
  /** The device could not draw the PDF (the page says to download it). */
  readonly onFail: () => void;
  /** Tests pass their own. */
  readonly open?: OpenPdf;
}): ReactElement {
  const [pages, setPages] = useState<PdfPage[] | null>(null);

  useEffect(() => {
    let live = true;
    let doc: PdfDocument | null = null;
    void (async () => {
      try {
        const opened = await open(new Uint8Array(await blob.arrayBuffer()));
        doc = opened;
        const all = await Promise.all(
          Array.from({ length: opened.numPages }, (_, i) => opened.getPage(i + 1)),
        );
        if (live) setPages(all);
      } catch {
        if (live) onFail();
      }
    })();
    return () => {
      live = false;
      void doc?.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one file
  }, [blob]);

  if (!pages) return <Loading what="the pages" />;
  return (
    <div className="docview__pages">
      {pages.map((p, i) => (
        <Page key={i} page={p} number={i + 1} of={pages.length} onFail={onFail} />
      ))}
    </div>
  );
}
